import { ChangeEvent, ComponentProps, useEffect, useRef, useState } from 'react'

import { Dropdown, Option, useNotifications } from '@joor/design-system'
import { useLocation } from 'react-router-dom'

import {
  getTextFromSelectedDropdownMultiOptions,
  getTextFromSelectedDropdownOption,
} from 'utils/formatters'
import { getUrlParam } from 'utils/mixpanel'
import { downloadFileFromResponse } from 'utils/requests'
import { useUserAnalytics } from 'utils/sdks/userAnalytics'
import { fromGlobalId } from 'utils/transformations/graphql'

import { CreateExcelOrderIds } from '../../orderImportModal.ids'
import {
  useConnectedAccounts,
  useInitialCreateOrderTemplateInfo,
  useRetailerPriceTypesAndDoors,
} from './StandardOrderTemplate.hooks'
import {
  Container,
  DownloadButton,
  DownloadDiv,
  DropdownContainer,
  HorizontalLine,
  MultiOptionsDropdown,
  OptionCheckbox,
  OptionsDropdown,
  OptionsLabel,
} from './StandardOrderTemplate.styles'
import {
  CreateExcelParams,
  RequestData,
  TemplateRequestData,
} from './StandardOrderTemplate.types'

const DEBOUNCE_TIME = 500

type DropdownProps = ComponentProps<typeof Dropdown>
interface StandardOrderProps {
  getDropdownProps: (
    key: string,
  ) => Pick<
    DropdownProps,
    'key' | 'menuPosition' | 'menuIsOpen' | 'onMenuOpen' | 'onMenuClose'
  >
}

const StandardOrder = ({
  getDropdownProps,
}: StandardOrderProps): JSX.Element => {
  const [fileIsDownloading, setFileIsDownloading] = useState<boolean>(false)
  const [includeImages, setIncludeImages] = useState<boolean>(false)

  const [retailerSearchText, setRetailerSearchText] = useState<string>('')
  const [retailerSearchQuery, setRetailerSearchQuery] =
    useState(retailerSearchText)
  const [chooseRetailerOnImport, setChooseRetailerOnImport] =
    useState<boolean>(false)

  const [selectedRetailer, setSelectedRetailer] = useState<Option | null>(null)
  const [selectedPriceType, setSelectedPriceType] = useState<Option | null>(
    null,
  )
  const [selectedDoors, setSelectedDoors] = useState<Option[]>([])
  const [selectedCollections, setSelectedCollections] = useState<Option[]>([])
  const [selectedEvent, setSelectedEvent] = useState<Option>()
  const [selectedOrderType, setSelectedOrderType] = useState<string>()

  const retailerSearchTimeout = useRef<NodeJS.Timeout | null>(null)

  const { connectedAccountsOptions, connectedAccountsQueryLoading } =
    useConnectedAccounts(retailerSearchText)

  const { addToasts } = useNotifications()
  const { trackEvent } = useUserAnalytics()
  const location = useLocation()

  const {
    initialCreateOrderTemplateLoading,
    initialCreateOrderTemplateError,
    collectionOptions,
    orderEventsOptions,
    orderTypesOptions,
    brandPriceTypeOptions,
  } = useInitialCreateOrderTemplateInfo()

  const {
    retailerPriceTypesAndDoorsLoading,
    retailerPriceTypesAndDoorsError,
    retailerDoorOptions,
    retailerPriceTypeOptions,
  } = useRetailerPriceTypesAndDoors(selectedRetailer?.value)

  const priceTypeOptions =
    chooseRetailerOnImport || !selectedRetailer
      ? brandPriceTypeOptions
      : retailerPriceTypeOptions

  const showingDoors: boolean =
    retailerDoorOptions.length > 0 && !chooseRetailerOnImport

  useEffect(() => {
    if (initialCreateOrderTemplateError) {
      addToasts([
        {
          title: 'Error',
          description: 'There was an error loading initial data',
          type: 'error',
        },
      ])
    }
  }, [initialCreateOrderTemplateError])

  useEffect(() => {
    if (retailerPriceTypesAndDoorsError) {
      addToasts([
        {
          title: 'Error',
          description:
            'There was an error loading Retailer Price Types and Doors',
          type: 'error',
        },
      ])
    }
  }, [retailerPriceTypesAndDoorsError])

  useEffect(() => {
    if (!selectedOrderType && orderTypesOptions.length > 0) {
      setSelectedOrderType(orderTypesOptions[0].value)
    }
  }, [selectedOrderType, orderTypesOptions])

  useEffect(() => {
    if (
      selectedPriceType ||
      !selectedRetailer ||
      !retailerPriceTypeOptions.length
    ) {
      return
    }
    setSelectedPriceType(retailerPriceTypeOptions[0])
  }, [retailerPriceTypeOptions, selectedPriceType, selectedRetailer])

  const isDownloadButtonDisabled = (): boolean => {
    return (
      (!chooseRetailerOnImport && !selectedRetailer) ||
      !selectedPriceType ||
      !selectedCollections.length
    )
  }

  const onRetailerInputChange = (inputText: string) => {
    setRetailerSearchQuery(inputText)
    if (retailerSearchTimeout.current) {
      clearTimeout(retailerSearchTimeout.current)
    }
    retailerSearchTimeout.current = setTimeout(() => {
      if (inputText !== retailerSearchText) {
        setRetailerSearchText(inputText)
      }
    }, DEBOUNCE_TIME)
  }

  const handleMultiOptionDropdownDeselect = (
    deselected: Option,
    previousValue: Option[],
    setter: (value: Option[]) => void,
  ) => {
    let newValue = [...previousValue]
    const index = previousValue.findIndex(
      (element) => element.value === deselected.value,
    )
    if (index < 0) return
    newValue.splice(index, 1)
    setter(newValue)
  }

  const handleChooseRetailerOnImport = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedRetailer) {
      const priceType = e.target.checked
        ? brandPriceTypeOptions[0]
        : retailerPriceTypeOptions[0]
      setSelectedPriceType(priceType)
    }
    setChooseRetailerOnImport(e.target.checked)
    setSelectedDoors([])
  }

  const getRequestData = (): RequestData => {
    const collectionIds = selectedCollections.map(
      (collection) => fromGlobalId(collection.value)?.id || '',
    )
    const priceTypeId = fromGlobalId(selectedPriceType?.value)?.id || ''

    let data: TemplateRequestData = {
      event: selectedEvent?.label,
      for_order_import: 1,
      include_images: includeImages,
      linesheet_ids: collectionIds,
      price_type_id: priceTypeId,
    }

    if (chooseRetailerOnImport) {
      return data
    }

    data.account_id = fromGlobalId(selectedRetailer?.value).id || ''

    if (selectedDoors.length > 0) {
      data.door_ids = selectedDoors.map(
        (selectedDoor) => fromGlobalId(selectedDoor?.value)?.id || '',
      )
    }

    return data
  }

  const downloadTemplate = (): Promise<string> =>
    downloadFileFromResponse('/collections/export/', getRequestData())

  const getCreateOrderTemplateExcelDownloadedParams =
    (): CreateExcelParams => ({
      Retailer: getTextFromSelectedDropdownOption(
        connectedAccountsOptions,
        selectedRetailer?.value,
      ),
      'Price Type': getTextFromSelectedDropdownOption(
        priceTypeOptions,
        selectedPriceType?.value,
      ),
      Linesheet: getTextFromSelectedDropdownMultiOptions(
        collectionOptions,
        selectedCollections.map((opt) => opt.value),
      ),
      Event: getTextFromSelectedDropdownOption(
        orderEventsOptions,
        selectedEvent?.value,
      ),
      ...getUrlParam(location),
    })

  const handlePrimaryActionClick = () => {
    trackEvent(
      'Orders List_Excel Order Template Downloaded',
      getCreateOrderTemplateExcelDownloadedParams(),
    )

    setFileIsDownloading(true)

    return downloadTemplate()
      .then((response) => {
        setFileIsDownloading(false)

        // @ts-expect-error
        if (response?.errors) {
          addToasts([
            {
              title: 'Error',
              description: 'There was an error downloading the template.',
              type: 'error',
            },
          ])
        } else {
          addToasts([
            {
              title: 'Downloading',
              description: 'Your template has been successfully downloaded.',
              type: 'success',
            },
          ])
        }
      })
      .catch((error) => {
        setFileIsDownloading(false)
        addToasts([
          {
            title: 'Error',
            description:
              'Template could not be downloaded. Please change price type and try again.',
            type: 'error',
          },
        ])
        console.error(error)
      })
  }

  return (
    <>
      <Container>
        <DropdownContainer>
          <OptionsDropdown
            id={CreateExcelOrderIds.RetailerDropdown}
            testId={CreateExcelOrderIds.RetailerDropdown}
            label="Search for a retailer"
            isRequired
            options={connectedAccountsOptions}
            isLoading={connectedAccountsQueryLoading}
            isDisabled={fileIsDownloading || chooseRetailerOnImport}
            inputValue={retailerSearchQuery}
            onInputChange={onRetailerInputChange}
            placeholder="Start typing to search..."
            value={selectedRetailer}
            onSelect={({ selected }: { selected: Option }) => {
              setSelectedRetailer(selected)
              setSelectedDoors([])
              setSelectedPriceType(null)
            }}
            translateOptions={false}
            isSearchable
            dropdownIndicatorIconName="search"
            isDropdownIndicatorIconAnimated={false}
            {...getDropdownProps('retailer')}
          />
          <OptionsDropdown
            id={CreateExcelOrderIds.PriceTypeDropdown}
            testId={CreateExcelOrderIds.PriceTypeDropdown}
            label="Search for a price type"
            isRequired
            isLoading={
              initialCreateOrderTemplateLoading ||
              retailerPriceTypesAndDoorsLoading
            }
            isDisabled={
              fileIsDownloading ||
              initialCreateOrderTemplateLoading ||
              retailerPriceTypesAndDoorsLoading
            }
            options={priceTypeOptions}
            value={selectedPriceType}
            onSelect={({ selected }: { selected: Option }) =>
              setSelectedPriceType(selected)
            }
            placeholder="Select one"
            {...getDropdownProps('priceType')}
          />
          <MultiOptionsDropdown
            id={CreateExcelOrderIds.LinesheetDropdown}
            testId={CreateExcelOrderIds.LinesheetDropdown}
            label="Search for a linesheet(s)"
            isRequired
            isLoading={initialCreateOrderTemplateLoading}
            isDisabled={fileIsDownloading || initialCreateOrderTemplateLoading}
            options={collectionOptions}
            value={selectedCollections}
            onSelect={({ selected }: { selected: Option }) =>
              setSelectedCollections([...selectedCollections, selected])
            }
            onDeselect={({ deselected }: { deselected: Option }) =>
              handleMultiOptionDropdownDeselect(
                deselected,
                selectedCollections,
                setSelectedCollections,
              )
            }
            placeholder="Select one"
            {...getDropdownProps('linesheet')}
          />
          {showingDoors && (
            <MultiOptionsDropdown
              id={CreateExcelOrderIds.DoorsDropdown}
              testId={CreateExcelOrderIds.DoorsDropdown}
              label="Search for a door"
              isLoading={retailerPriceTypesAndDoorsLoading}
              isDisabled={
                fileIsDownloading || retailerPriceTypesAndDoorsLoading
              }
              options={retailerDoorOptions}
              value={selectedDoors}
              onSelect={({ selected }: { selected: Option }) =>
                setSelectedDoors([...selectedDoors, selected])
              }
              onDeselect={({ deselected }: { deselected: Option }) =>
                handleMultiOptionDropdownDeselect(
                  deselected,
                  selectedDoors,
                  setSelectedDoors,
                )
              }
              placeholder="Select one"
              {...getDropdownProps('door')}
            />
          )}
          <OptionsDropdown
            id={CreateExcelOrderIds.EventsDropdown}
            testId={CreateExcelOrderIds.EventsDropdown}
            label="Search for an event"
            nullable
            options={orderEventsOptions}
            isLoading={initialCreateOrderTemplateLoading}
            isDisabled={fileIsDownloading || initialCreateOrderTemplateLoading}
            value={selectedEvent}
            onSelect={({ selected }: { selected: Option }) =>
              setSelectedEvent(selected)
            }
            placeholder="Select one"
            {...getDropdownProps('event')}
          />
        </DropdownContainer>
        <div>
          <OptionsLabel>Options</OptionsLabel>
          <OptionCheckbox
            id={CreateExcelOrderIds.RetailerImportCheckboxId}
            label="Choose retailer on import"
            checked={chooseRetailerOnImport}
            onChange={handleChooseRetailerOnImport}
            fontWeight="medium"
            disabled={fileIsDownloading}
          />
          <OptionCheckbox
            id={CreateExcelOrderIds.IncludeImagesCheckboxId}
            label="Include images"
            checked={includeImages}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setIncludeImages(e.target.checked)
            }
            fontWeight="medium"
            disabled={fileIsDownloading}
          />
        </div>
      </Container>
      <HorizontalLine />
      <DownloadDiv>
        <DownloadButton
          onClick={handlePrimaryActionClick}
          disabled={isDownloadButtonDisabled()}
          loading={fileIsDownloading}
          data-testid="download-button"
          id={CreateExcelOrderIds.DownloadTemplateButtonId}
        >
          Download Template
        </DownloadButton>
      </DownloadDiv>
    </>
  )
}

export default StandardOrder
